import React from 'react';
import Layout from "../temp/layout/Layout";
import Counter from "../../components/global/counter/Counter";
import { Helmet } from 'react-helmet';
import { 
    FaGraduationCap, FaRocket, FaShieldAlt, FaUsers, 
    FaChartLine, FaAward, FaHandshake, FaLightbulb,
    FaCheckCircle, FaArrowRight
} from 'react-icons/fa';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const About = () => {
    useEffect(() => {
        AOS.init({ 
            duration: 1000, 
            once: true,
            offset: 100
        });
    }, []);

    const values = [
        {
            icon: <FaRocket />,
            title: "Innovation",
            description: "Constantly evolving with cutting-edge technology to stay ahead of educational trends."
        },
        {
            icon: <FaShieldAlt />,
            title: "Security",
            description: "High-grade security ensuring your institution's data is always protected."
        },
        {
            icon: <FaHandshake />,
            title: "Partnership",
            description: "We work closely with institutions as partners in their digital transformation journey."
        },
        {
            icon: <FaLightbulb />,
            title: "Excellence",
            description: "Committed to delivering excellence in every feature and interaction."
        }
    ];

    const achievements = [
        {
            icon: <FaGraduationCap />,
            number: 20,
            suffix: "+",
            label: "Educational Institutions"
        },
        {
            icon: <FaUsers />,
            number: 55000,
            suffix: "+",
            label: "Active Users"
        },
        {
            icon: <FaAward />,
            number: 100,
            suffix: "+",
            label: "Expert Team Members"
        },
        {
            icon: <FaChartLine />,
            number: 10,
            suffix: "+",
            label: "Cities Served"
        }
    ];

    return (
        <Layout>
            <Helmet>
                <title>About Us - Educational Eternity | Leading Education ERP System in India</title>
                <meta name="description" content="Learn about Educational Eternity - India's leading AI-powered Education ERP system. Our mission to revolutionize education through innovative technology and digital transformation." />
                <meta name="keywords" content="About Educational Eternity, Education ERP Company, School Management Software Provider, Educational Technology Company India" />
            </Helmet>

            {/* Hero Section */}
            <section className="relative w-full h-[60vh] bg-cover bg-center flex items-center justify-center overflow-hidden"
                style={{ backgroundImage: `url(assets/images/newabout.jpg)` }}>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-indigo-900/80"></div>
                <div className="relative z-10 text-center px-4">
                    <h1 data-aos="fade-up" className="font-bold text-5xl md:text-7xl text-white mb-4">
                        About Us
                    </h1>
                    <p data-aos="fade-up" data-aos-delay="100" className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                        Revolutionizing Education Through Innovation
                    </p>
                </div>
            </section>

            {/* Mission Section */}
            <section className="pt-20 pb-20 lg:pt-32 lg:pb-32 relative bg-white px-4 sm:px-8">
                <div className="container mx-auto">
                    <div className="flex flex-col lg:flex-row justify-between items-center gap-12">
                        <div className="lg:w-1/2" data-aos="fade-right">
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                <img 
                                    src="assets/images/Aboutnew.jpeg" 
                                    alt="About Educational Eternity" 
                                    className="w-full h-auto object-cover rounded-2xl" 
                                />
                            </div>
                        </div>
                        <div className="lg:w-1/2 p-6 lg:p-10" data-aos="fade-left">
                            <div className="mb-6">
                                <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
                                    Our Mission
                                </span>
                                <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
                                    Revolutionizing Education with Digital Solutions
                                </h2>
                                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                    At Educational Eternity, we believe that the future of education is digital. Our mission is to empower schools and colleges by providing a seamless, all-in-one digital platform that simplifies administrative tasks, enhances learning experiences, and fosters effective communication.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    With a deep understanding of the challenges faced by educational institutions, we have developed a smart, efficient, and user-friendly system that automates processes, reduces manual work, and boosts productivity. We're not just a software company—we're your partners in educational transformation.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Vision Section */}
            <section className="pt-10 pb-10 lg:pt-24 lg:pb-24 px-4 sm:px-8 bg-gray-50">
                <div className="container mx-auto">
                    <div className="lg:flex lg:justify-between lg:items-center gap-12">
                        <div className="lg:w-1/2 mb-10 lg:mb-0" data-aos="fade-right">
                            <div className="mb-6">
                                <span className="inline-block px-4 py-2 bg-indigo-100 text-indigo-800 rounded-full text-sm font-semibold mb-4">
                                    Our Vision
                                </span>
                                <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
                                    A Fully Digitized Educational Ecosystem
                                </h2>
                                <p className="text-lg text-gray-700 leading-relaxed mb-4">
                                    In the modern world, education is more than just teaching and learning—it involves managing complex administrative processes, ensuring smooth communication between stakeholders, and maintaining a structured academic environment. However, many schools and colleges still rely on manual, time-consuming methods that create inefficiencies, errors, and unnecessary workload.
                                </p>
                                <p className="text-lg text-gray-700 leading-relaxed">
                                    At Educational Eternity, we envision a fully digitized educational ecosystem—a world where institutions can focus entirely on education while we take care of administration, data management, and automation with cutting-edge technology. We're building the future of education, one institution at a time.
                                </p>
                            </div>
                        </div>
                        <div className="lg:w-1/2" data-aos="fade-left">
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                <img 
                                    src="assets/images/about1.jpeg" 
                                    alt="Educational Vision" 
                                    className="w-full h-auto object-cover rounded-2xl" 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="pt-20 pb-20 lg:pt-32 lg:pb-32 relative bg-white px-4 sm:px-8">
                <div className="container mx-auto">
                    <div className="flex flex-col lg:flex-row justify-between items-center gap-12">
                        <div className="lg:w-1/2" data-aos="fade-right">
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                <img 
                                    src="assets/images/about12.jpeg" 
                                    alt="Why Choose Us" 
                                    className="w-full h-auto object-cover rounded-2xl" 
                                />
                            </div>
                        </div>
                        <div className="lg:w-1/2 p-6 lg:p-10" data-aos="fade-left">
                            <div className="mb-6">
                                <span className="inline-block px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold mb-4">
                                    Why Choose Us
                                </span>
                                <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
                                    What Sets Educational Eternity Apart?
                                </h2>
                                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                                    We offer a comprehensive digital solution that transforms how educational institutions operate. Here's what makes us different:
                                </p>
                                <div className="space-y-4">
                                    {[
                                        "End-to-End Digital Solutions - Complete suite of tools for managing academics, admissions, finances, attendance, communication, and student records—all in one platform.",
                                        "Automation & Efficiency - Say goodbye to manual paperwork. Our system automates routine tasks, reducing administrative burden by up to 80%.",
                                        "Seamless Communication - Bridging the gap between teachers, students, and parents with instant updates, notifications, and collaboration through mobile-friendly interfaces.",
                                        "Data-Driven Decision Making - Real-time insights on student performance, attendance trends, and financial reports, enabling smarter decision-making.",
                                        "High-Grade Security - Your institution's sensitive data is protected with enterprise-level security and compliance."
                                    ].map((point, idx) => (
                                        <div key={idx} className="flex items-start">
                                            <FaCheckCircle className="text-green-500 text-xl mr-3 mt-1 flex-shrink-0" />
                                            <p className="text-gray-700 leading-relaxed">{point}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Values Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16" data-aos="fade-up">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                            Our Core Values
                        </h2>
                        <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                            The principles that guide everything we do
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
                        {values.map((value, idx) => (
                            <div
                                key={idx}
                                data-aos="fade-up"
                                data-aos-delay={idx * 100}
                                className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 text-center hover:bg-white/20 transition-all duration-300"
                            >
                                <div className="text-5xl text-yellow-400 mb-6 flex justify-center">
                                    {value.icon}
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-4">{value.title}</h3>
                                <p className="text-blue-100 leading-relaxed">{value.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Join Us Section */}
            <section className="pt-24 pb-24 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center" data-aos="fade-up">
                        <div className="bg-white rounded-2xl shadow-xl p-12 border border-gray-100">
                            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                                Join Us in Transforming Education!
                            </h2>
                            <p className="text-lg text-gray-700 leading-relaxed mb-8">
                                At Educational Eternity, we believe in empowering institutions to embrace the future of education. Whether you're a school administrator, principal, or educator, our digital solutions will help you streamline operations, improve engagement, and enhance educational experiences for all.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link to="/contact">
                                    <button className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full text-lg font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2">
                                        Get in Touch <FaArrowRight />
                                    </button>
                                </Link>
                                <Link to="/plans">
                                    <button className="px-8 py-4 bg-white border-2 border-blue-600 text-blue-600 rounded-full text-lg font-bold hover:bg-blue-50 transform hover:scale-105 transition-all duration-300">
                                        View Plans
                                    </button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Statistics Section */}
            <section className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 py-20">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12" data-aos="fade-up">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            Our Impact in Numbers
                        </h2>
                        <p className="text-xl text-blue-100">
                            Growing together with educational institutions across India
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
                        {achievements.map((achievement, index) => (
                            <div 
                                key={index} 
                                data-aos="fade-up" 
                                data-aos-delay={index * 100}
                                className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 text-center"
                            >
                                <div className="text-4xl text-yellow-400 mb-4 flex justify-center">
                                    {achievement.icon}
                                </div>
                                <div className="text-5xl font-bold text-white mb-2">
                                    <Counter end={achievement.number} />{achievement.suffix}
                                </div>
                                <div className="text-blue-200 font-semibold text-lg">
                                    {achievement.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default About;
